import React, { useCallback } from "react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { ActivityImageGallery } from "../components/ActivityImages";
import {
  ActivityTypePill,
  BooleanIndicator,
  ContentSection,
  DisplayValue,
  RecordDetailCard,
  RecordFieldRow,
  StatusPill,
} from "../components/DataDisplay";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { PortalLink } from "../components/PortalLink";
import { templateStepsEditorPath } from "../config/activityStepsNavigation";
import { activityTemplateEditPath } from "../config/navigation";
import {
  activityTemplatesReturnToFromSearch,
  withActivityTemplatesReturnTo,
} from "../config/activityTemplateNavigation";
import { usePortalData } from "../hooks/usePortalData";
import { formatPortalDateTime } from "../utils/dateTime";

export function ActivityTemplateDetailPage({
  clinicianCode,
  templateId,
}: {
  clinicianCode: string;
  templateId: string;
}) {
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getActivityTemplate(clinicianCode, templateId, signal),
    [clinicianCode, templateId],
  );
  const state = usePortalData(request);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.success &&
      data.found &&
      data.authorized &&
      data.therapist &&
      data.template,
  );
  const template = available ? data?.template : null;
  const returnTo = activityTemplatesReturnToFromSearch(window.location.search);

  return (
    <ClinicianPortalLayout
      activeGlobalKey="templates"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title={template?.name || "Activity Template Detail"}
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              {
                label: "Activity Templates",
                path: returnTo,
              },
              { label: template?.name || "Template Detail" },
            ]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--profile">
        {state.status === "loading" ? (
          <LoadingState label="Loading activity template" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState
            error={
              new Error(data.error || "Activity template is not available")
            }
          />
        ) : null}
        {available && template ? (
          <>
            <ContentSection
              title="Activity Template Details"
              actions={
                <PortalLink
                  className="cp-button cp-button--primary"
                  path={withActivityTemplatesReturnTo(
                    activityTemplateEditPath(templateId),
                    returnTo,
                  )}
                  clinicianCode={clinicianCode}
                >
                  Edit Template
                </PortalLink>
              }
            >
              <RecordDetailCard>
                <RecordFieldRow label="Activity Template">
                  <DisplayValue value={template.name} />
                </RecordFieldRow>
                <RecordFieldRow label="Description">
                  <span className="cp-multiline-value">
                    <DisplayValue value={template.description} />
                  </span>
                </RecordFieldRow>
                <RecordFieldRow label="Activity Type">
                  <ActivityTypePill activityType={template.activityType} />
                </RecordFieldRow>
                <RecordFieldRow label="Exposure Homework">
                  <BooleanIndicator value={!template.nonExposureHomework} />
                </RecordFieldRow>
                <RecordFieldRow label="Activity Steps">
                  <div className="cp-detail-value-with-action">
                    <span className="cp-multiline-value">
                      <DisplayValue value={template.activitySteps} />
                    </span>
                    <a
                      className="cp-edit-steps-button"
                      href={templateStepsEditorPath({
                        clinicianCode,
                        templateId,
                        returnUrl: window.location.href,
                      })}
                    >
                      Edit Steps
                    </a>
                  </div>
                </RecordFieldRow>
                <RecordFieldRow label="Modification Steps">
                  <span className="cp-multiline-value">
                    <DisplayValue value={template.alternative} />
                  </span>
                </RecordFieldRow>
                <RecordFieldRow label="Coaching Prompts">
                  <span className="cp-multiline-value">
                    <DisplayValue value={template.coachingPrompts} />
                  </span>
                </RecordFieldRow>
                <RecordFieldRow label="Activity Images">
                  <ActivityImageGallery images={template.activityImages} />
                </RecordFieldRow>
                <RecordFieldRow label="Activity Points">
                  <DisplayValue value={template.points} />
                </RecordFieldRow>
                <RecordFieldRow label="Activity Coins">
                  <DisplayValue value={template.coins} />
                </RecordFieldRow>
              </RecordDetailCard>
            </ContentSection>

            <ContentSection title="Template Settings">
              <RecordDetailCard>
                <RecordFieldRow label="Status">
                  <StatusPill status={template.status} />
                </RecordFieldRow>
                <RecordFieldRow label="Protected">
                  <BooleanIndicator value={template.protected} />
                </RecordFieldRow>
                <RecordFieldRow label="Created">
                  {formatPortalDateTime(template.createdAt)}
                </RecordFieldRow>
                <RecordFieldRow label="Updated">
                  {formatPortalDateTime(template.updatedAt)}
                </RecordFieldRow>
              </RecordDetailCard>
            </ContentSection>
          </>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
